import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  ButtonComponent,
  DatePickerComponent,
  IPaginationEvent,
  ModalService,
  MultiselectComponent,
  PaginationComponent,
  SvgIconComponent,
  TableColumnItem,
  TableComponent,
} from '@ui-lib/components';
import { AsyncPipe, DatePipe } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  IMaintenanceFormFilters,
  TMaintenanceFormFiltersKeys,
  TSelectedFilters,
} from './maintenance.model';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { getMaintenanceTableColumnsConfig } from './maintenance-table-columns.config';
import { MaintenanceService } from './maintenance.service';
import { LoadingState } from '../../shared/types/loading.type';
import { filter, take } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MaintenanceRequestModalComponent } from './components/maintenance-request-modal/maintenance-request-modal.component';
import { prepareRequest } from '../../shared/utils/prepare-request.function';
import { FacilitySelectorComponent } from '../../shared/components/facility-selector/facility-selector.component';
import { SearchMaintenanceRequestsResponse } from '../../core/openapi/bff-admin-api';
import { ServiceCenterSelectorComponent } from '../../shared/components/service-center-selector/service-center-selector.component';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { DeviceListService } from '../devices/views/device-list/device-list.service';
import { TelemetrySignalRService } from '../../core/services/telemetry/telemetry-signalr.service';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';

const UNKNOWN_EQUIPMENT_ID = 'unknown';

@Component({
  standalone: true,
  selector: 'mon-maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MaintenanceService, DeviceListService, TelemetrySignalRService],
  imports: [
    AsyncPipe,
    ButtonComponent,
    DatePickerComponent,
    DatePipe,
    FacilitySelectorComponent,
    FormsModule,
    MultiselectComponent,
    PageToolbarComponent,
    PaginationComponent,
    ReactiveFormsModule,
    RouterOutlet,
    ServiceCenterSelectorComponent,
    SvgIconComponent,
    TableComponent,
    TranslateModule,
  ],
})
export class MaintenanceComponent implements OnInit {
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  public readonly LoadingState = LoadingState;
  public readonly columns: TableColumnItem[] = getMaintenanceTableColumnsConfig();

  public readonly currentPage: WritableSignal<number> = signal(1);
  public readonly maintenanceTotalCount: Signal<number> =
    this.maintenanceService.maintenanceTotalCount;
  public readonly maintenanceLoadingStatus: Signal<LoadingState> =
    this.maintenanceService.maintenanceLoadingStatus;

  public readonly maintenance$ = this.maintenanceService.maintenance$;
  public readonly statuses$ = this.maintenanceService.statuses$;
  public readonly maintenanceTypes$ = this.maintenanceService.maintenanceTypes$;
  public readonly warrantyTypes$ = this.maintenanceService.warrantyTypes$;
  protected readonly canManageMaintenance = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.MaintenanceManage),
    { initialValue: false },
  );
  protected readonly canReadEquipment = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.EquipmentRead),
    { initialValue: false },
  );
  protected readonly canReadFacilities = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FacilityRead),
    { initialValue: false },
  );

  private readonly allStatuses = toSignal(this.statuses$);
  private readonly allMaintenanceTypes = toSignal(this.maintenanceTypes$);
  private readonly allWarrantyTypes = toSignal(this.warrantyTypes$);

  protected readonly asMaintenance = (item: unknown): SearchMaintenanceRequestsResponse =>
    item as SearchMaintenanceRequestsResponse;

  protected readonly displayStatus = (
    status: SearchMaintenanceRequestsResponse['status'],
  ): string => (status != null ? `monitoring.maintenance.status.${status}` : '-');

  protected readonly displayError = (error: SearchMaintenanceRequestsResponse['error']): string =>
    error != null ? `monitoring.maintenance.error.${error}` : '-';

  protected readonly displayMaintenanceType = (
    maintenanceType: SearchMaintenanceRequestsResponse['maintenanceType'],
  ): string =>
    maintenanceType != null ? `monitoring.maintenance.maintenanceType.${maintenanceType}` : '-';

  protected readonly displayWarrantyType = (
    warrantyType: SearchMaintenanceRequestsResponse['warrantyType'],
  ): string => (warrantyType != null ? `monitoring.maintenance.warrantyType.${warrantyType}` : '-');

  public readonly maintenanceFormFilters = new FormGroup<IMaintenanceFormFilters>({
    keyword: new FormControl<string>('', { nonNullable: true }),
    statusIds: new FormControl<number[]>([], { nonNullable: true }),
    maintenanceTypeIds: new FormControl<number[]>([], { nonNullable: true }),
    facilityIds: new FormControl<string[]>([], { nonNullable: true }),
    serviceCenterIds: new FormControl<string[]>([], { nonNullable: true }),
    warrantyTypeIds: new FormControl<number[]>([], { nonNullable: true }),
    datePeriod: new FormControl<Date[] | null>(null),
  });

  protected readonly minDateAvailable = new Date(2025, 0, 1);
  protected readonly maxDateAvailable = new Date();

  protected selectedFacilities: IDictionaryItem<string>[] = [];
  protected selectedServiceCenters: IDictionaryItem<string>[] = [];

  public get facilityIdsControl(): FormControl<string[]> {
    return this.maintenanceFormFilters.get('facilityIds') as FormControl<string[]>;
  }

  public get serviceCenterIdsControl(): FormControl<string[]> {
    return this.maintenanceFormFilters.get('serviceCenterIds') as FormControl<string[]>;
  }

  public get keywordControl(): FormControl<string> {
    return this.maintenanceFormFilters.get('keyword') as FormControl<string>;
  }

  protected get toolbarActions(): PageToolbarAction[] {
    if (!this.canManageMaintenance()) return [];

    return [
      {
        id: 'create',
        icon: 'plus',
        titleTranslateKey: 'monitoring.maintenance.add-request',
      },
    ];
  }

  protected get selectedStatuses(): IDictionaryItem<number>[] {
    const selectedIds = new Set(this.getMaintenanceFiltersControl<number[]>('statusIds').value);
    return this.allStatuses()?.filter((item) => item.id != null && selectedIds.has(item.id)) ?? [];
  }

  protected get selectedWarrantyTypes(): IDictionaryItem<number>[] {
    const selectedIds = new Set(
      this.getMaintenanceFiltersControl<number[]>('warrantyTypeIds').value,
    );
    return (
      this.allWarrantyTypes()?.filter((item) => item.id != null && selectedIds.has(item.id)) ?? []
    );
  }

  protected get selectedMaintenanceTypes(): IDictionaryItem<number>[] {
    const selectedIds = new Set(
      this.getMaintenanceFiltersControl<number[]>('maintenanceTypeIds').value,
    );
    return (
      this.allMaintenanceTypes()?.filter((item) => item.id != null && selectedIds.has(item.id)) ??
      []
    );
  }

  public get isFiltersActive(): boolean {
    const {
      keyword,
      statusIds,
      maintenanceTypeIds,
      facilityIds,
      serviceCenterIds,
      warrantyTypeIds,
      datePeriod,
    } = this.maintenanceFormFilters.getRawValue();
    return (
      keyword.trim().length > 0 ||
      statusIds.length > 0 ||
      maintenanceTypeIds.length > 0 ||
      (this.canReadFacilities() && facilityIds.length > 0) ||
      serviceCenterIds.length > 0 ||
      warrantyTypeIds.length > 0 ||
      datePeriod !== null
    );
  }

  public get allSelectedFilters(): TSelectedFilters[] {
    const filters: TSelectedFilters[] = [];
    const dateValue = this.getMaintenanceFiltersControl<Date[] | null>('datePeriod').value;
    const selectedDate = dateValue
      ? [
          {
            id: 'datePeriod',
            name:
              dateValue[0].toLocaleDateString('ru-RU') +
              ' - ' +
              dateValue[1].toLocaleDateString('ru-RU'),
          },
        ]
      : [];

    if (this.selectedStatuses.length > 0) {
      filters.push(['statusIds', this.selectedStatuses]);
    }
    if (this.selectedMaintenanceTypes.length > 0) {
      filters.push(['maintenanceTypeIds', this.selectedMaintenanceTypes]);
    }
    if (this.canReadFacilities() && this.selectedFacilities.length > 0) {
      filters.push(['facilityIds', this.selectedFacilities]);
    }
    if (this.selectedServiceCenters.length > 0) {
      filters.push(['serviceCenterIds', this.selectedServiceCenters]);
    }
    if (this.selectedWarrantyTypes.length > 0) {
      filters.push(['warrantyTypeIds', this.selectedWarrantyTypes]);
    }
    if (selectedDate.length > 0) {
      filters.push(['datePeriod', selectedDate]);
    }

    return filters;
  }

  public get selectedFiltersCount(): number {
    return this.allSelectedFilters.reduce((count, [, filters]) => count + filters.length, 0);
  }

  public ngOnInit(): void {
    this.applyFilters();

    this.maintenanceFormFilters.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentPage.set(1);
        this.applyFilters();
      });
  }

  public getMaintenanceFiltersControl<T>(controlName: TMaintenanceFormFiltersKeys): FormControl<T> {
    return this.maintenanceFormFilters.get(controlName) as FormControl<T>;
  }

  public onPageChange(paginator: IPaginationEvent): void {
    this.currentPage.set(paginator.pageIndex);
    this.maintenanceService.changeReqParams({
      skip: (paginator.pageIndex - 1) * paginator.pageSize,
      take: paginator.pageSize,
    });
  }

  public clearAllFilters(): void {
    if (!this.isFiltersActive) return;

    this.maintenanceFormFilters.setValue({
      keyword: '',
      statusIds: [],
      maintenanceTypeIds: [],
      facilityIds: [],
      serviceCenterIds: [],
      warrantyTypeIds: [],
      datePeriod: null,
    });
  }

  public deselectFilter(
    controlName: Exclude<TMaintenanceFormFiltersKeys, 'keyword'>,
    removedItem: IDictionaryItem<string | number>,
  ): void {
    if (controlName === 'datePeriod') {
      this.getMaintenanceFiltersControl<Date[] | null>(controlName).setValue(null);
      return;
    }

    if (controlName === 'facilityIds' || controlName === 'serviceCenterIds') {
      const control = this.getMaintenanceFiltersControl<string[]>(controlName);
      control.setValue(control.value.filter((id) => id !== removedItem.id));
      return;
    }

    const currentControl = this.getMaintenanceFiltersControl<number[]>(controlName);
    currentControl.setValue(currentControl.value.filter((id) => id !== removedItem.id));
  }

  public onFacilitySelectionChange(items: IDictionaryItem<string>[]): void {
    this.selectedFacilities = items;
  }

  public onServiceCenterSelectionChange(items: IDictionaryItem<string>[]): void {
    this.selectedServiceCenters = items;
  }

  public openRequestModal(): void {
    if (!this.canManageMaintenance()) {
      return;
    }

    this.modalService
      .open({
        component: MaintenanceRequestModalComponent,
        customContainerClass: 'maintenance-request-modal',
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result: boolean | undefined): result is true => !!result),
      )
      .subscribe(() => {
        this.maintenanceService.changeReqParams({});
      });
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.openRequestModal();
    }
  }

  public openDeviceCard(request: SearchMaintenanceRequestsResponse): void {
    const serialNumber = request.serialNumber?.trim();
    if (!serialNumber) return;

    if (!this.canReadEquipment()) {
      this.openMaintenanceOnlyCard();
      return;
    }

    this.maintenanceService
      .getEquipmentBySerialNumber(serialNumber)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((equipment) => {
        this.router.navigate([equipment?.id ?? UNKNOWN_EQUIPMENT_ID, 'maintenance'], {
          relativeTo: this.route,
          queryParams: {
            ...(!equipment?.id || equipment.isDeleted ? { maintenanceOnly: true } : {}),
          },
        });
      });
  }

  private openMaintenanceOnlyCard(): void {
    void this.router.navigate([UNKNOWN_EQUIPMENT_ID, 'maintenance'], {
      relativeTo: this.route,
      queryParams: {
        maintenanceOnly: true,
      },
    });
  }

  private applyFilters(): void {
    const filters = this.maintenanceFormFilters.getRawValue();
    const preparedFilters = prepareRequest(filters);

    this.maintenanceService.changeReqParams({
      skip: 0,
      keyword: preparedFilters.keyword,
      statusIds: preparedFilters.statusIds,
      maintenanceTypeIds: preparedFilters.maintenanceTypeIds,
      facilityIds: this.canReadFacilities() ? preparedFilters.facilityIds : undefined,
      serviceCenterIds: preparedFilters.serviceCenterIds,
      warrantyTypeIds: preparedFilters.warrantyTypeIds,
      datePeriod: preparedFilters.datePeriod
        ? {
            startDateTime: preparedFilters.datePeriod[0]?.toISOString(),
            endDateTime: preparedFilters.datePeriod[1]?.toISOString(),
          }
        : undefined,
    });
  }
}
